import { useEffect, useCallback, useRef, useState, useMemo, Fragment } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createPageUrl } from '@/components/utils';
import { PERMS } from '@/constants/permissions';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { navigationHref, navigationId, visibleNavigation, isNavigationItemVisible, navigationSearchItems } from '@/lib/navigation';
import { navigation as defaultNavigation } from '@/lib/navigationTree';
import { usePermissions } from '@/components/hooks/usePermissions';
import ResultadosBusqueda, { MIN_CARACTERES } from '@/components/command/ResultadosBusqueda';
import {
  Search,
  Plus,
  FileText,
  ScanLine,
  FolderKanban,
  ArrowUp,
  ArrowDown,
  CornerDownLeft,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Data — all navigation items grouped by category                    */
/* ------------------------------------------------------------------ */

/*
 * `permission`: código que exige el `PermissionGuard` de la ruta destino
 * (resolviendo los redirects intermedios). Se anota aquí porque la paleta no
 * filtraba nada y ofrecía pantallas que el guard rechaza acto seguido: elegir
 * un destino y toparse con un bloqueo enseña a no volver a usar ⌘K.
 *
 * La regla al anotar es que la paleta NUNCA sea más estricta que la app: si la
 * ruta no declara guard, aquí tampoco va `permission` y el item se ve siempre.
 * Un permiso inventado aquí escondería una pantalla que el usuario sí puede
 * abrir, que es peor que el problema que se está arreglando.
 */

const quickActions = [
  { labelKey: 'command.new_invoice',  page: 'FacturaForm',  icon: Plus, permission: PERMS.INVOICING_VIEW },
  { labelKey: 'command.new_expense',  page: 'Gastos',       icon: Plus, params: { action: 'new' }, permission: PERMS.ACCOUNTING_VIEW },
  { labelKey: 'command.new_customer', page: 'Contactos',    icon: Plus, params: { action: 'new' } },
  { labelKey: 'command.new_contact',  page: 'Contactos',    icon: Plus, params: { action: 'new' } },
  { labelKey: 'command.new_project',  page: 'Projects',     icon: Plus, params: { action: 'new' } },
];

// Documentación. Va en su propio grupo porque es lo que menos se encuentra por
// menú: "presupuesto" vive en dos sitios distintos según quién lo emitió, y el
// archivo está dentro de la Bandeja, que suena a cola de trabajo.
// Los `keywords` son la vía de entrada real — la gente teclea "poliza" o
// "albaran", no el nombre de la pantalla. Con y sin tilde: cmdk compara literal.
const canSeeDocuments = ctx => ctx.hasPermission('ocr.review') || ctx.hasPermission('documents.view');
const documentsItems = [
  {
    labelKey: 'command.documents_archive', page: 'BandejaDocumentos', icon: FolderKanban, showIf: canSeeDocuments,
    params: { tab: 'biblioteca' },
    keywords: ['documentos', 'archivo', 'archivados', 'póliza', 'poliza', 'seguro',
      'contrato', 'escritura', 'certificado', 'albarán', 'albaran', 'recibo', 'ticket',
      'justificante', 'nota de entrega'],
  },
  {
    labelKey: 'command.quotes_received', page: 'BandejaDocumentos', icon: FileText, showIf: canSeeDocuments,
    params: { tab: 'presupuestos' },
    keywords: ['presupuesto', 'presupuestos', 'recibido', 'recibidos', 'oferta',
      'ofertas', 'proforma', 'proveedor', 'compra'],
  },
  {
    labelKey: 'command.quotes_issued', page: 'Facturas', icon: FileText, permission: PERMS.INVOICING_VIEW,
    params: { tab: 'presupuestos' },
    keywords: ['presupuesto', 'presupuestos', 'emitido', 'emitidos', 'oferta',
      'cliente', 'venta', 'quote'],
  },
  {
    labelKey: 'nav.documents_inbox', page: 'BandejaDocumentos', icon: ScanLine, showIf: canSeeDocuments,
    keywords: ['bandeja', 'pendientes', 'ocr', 'escaneo', 'subir', 'email'],
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function CommandMenu({ open, onOpenChange, navigation = defaultNavigation, navigationContext }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const navCtx = useMemo(() => navigationContext || { hasPermission, user: null, userCompaniesCount: 0, onboardingDone: true }, [navigationContext, hasPermission]);
  const shared = visibleNavigation(navigation, navCtx);
  const asCommand = item => ({ ...item, labelKey: item.nameKeyFor ? item.nameKeyFor(navCtx) : item.nameKey,
    page: item.pageName, path: navigationHref(item, navCtx), id: navigationId(item),
    keywords: [item.descriptionKey ? t(item.descriptionKey) : '', item.pageName, ...documentsItems.filter(alias => createPageUrl(alias.page, alias.params) === navigationHref(item, navCtx)).flatMap(alias => alias.keywords || [])] });
  const sharedGroups = [
    [t('common.navigation'), navigationSearchItems(shared.filter(item => !item.subItems)).filter(item => isNavigationItemVisible(item, navCtx)).map(asCommand)],
    ...shared.filter(item => item.subItems).map(item => [t(item.nameKey), navigationSearchItems(item.subItems).filter(sub => isNavigationItemVisible(sub, navCtx)).map(asCommand)]),
  ];
  const canonicalHrefs = new Set(sharedGroups.flatMap(([, items]) => items.map(item => item.path)));
  const extraDocuments = documentsItems.filter(item => !canonicalHrefs.has(createPageUrl(item.page, item.params)));
  const inputRef = useRef(null);
  const [busqueda, setBusqueda] = useState('');

  // Al cerrar se olvida el término: reabrir ⌘K con la búsqueda anterior confunde.
  useEffect(() => {
    if (!open) setBusqueda('');
  }, [open]);

  /* --- ocultar lo que el guard de la ruta rechazaría --- */
  const visible = useCallback(
    (items) => items.filter((it) => isNavigationItemVisible(it, navCtx)),
    [navCtx],
  );

  /* --- keyboard shortcut: Cmd+K / Ctrl+K --- */
  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange((prev) => !prev);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [onOpenChange]);

  /* --- run a command and close the palette --- */
  const runCommand = useCallback(
    (callback) => {
      onOpenChange(false);
      callback();
    },
    [onOpenChange],
  );

  /* --- helper: navigate to page --- */
  const go = useCallback(
    (page, params) => () => navigate(createPageUrl(page, params)),
    [navigate],
  );

  /* --- render a single item row --- */
  const renderItem = ({ labelKey, page, icon: Icon, shortcut, params, keywords, path, id }) => (
    <Command.Item
      key={id || `${page}-${labelKey}`}
      value={t(labelKey)}
      keywords={keywords}
      onSelect={() => runCommand(path ? () => navigate(path) : go(page, params))}
      className="group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm
        text-foreground
        outline-none cursor-pointer select-none
        aria-selected:bg-blue-50 aria-selected:text-blue-700
        dark:aria-selected:bg-blue-950/40 dark:aria-selected:text-blue-300
        transition-colors duration-100"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md
        bg-muted
        group-aria-selected:bg-blue-100 dark:group-aria-selected:bg-blue-900/50
        transition-colors duration-100"
      >
        <Icon className="h-4 w-4 text-muted-foreground
          group-aria-selected:text-blue-600 dark:group-aria-selected:text-blue-400
          transition-colors duration-100"
        />
      </span>
      <span className="flex-1 truncate font-medium">{t(labelKey)}</span>
      {shortcut && (
        <kbd className="ml-auto hidden rounded-md border border-border
          bg-muted px-1.5 py-0.5 text-micro font-mono
          text-muted-foreground sm:inline-block">
          {shortcut}
        </kbd>
      )}
    </Command.Item>
  );

  /* --- render a group --- */
  const renderGroup = (heading, items) => (
    <Command.Group
      key={heading}
      heading={heading}
      className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2
        [&_[cmdk-group-heading]]:text-micro [&_[cmdk-group-heading]]:font-semibold
        [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider
        [&_[cmdk-group-heading]]:text-muted-foreground dark:[&_[cmdk-group-heading]]:text-muted-foreground
        px-2 pb-1"
    >
      {items.map(renderItem)}
    </Command.Group>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 overflow-hidden shadow-2xl border-0
          max-w-[560px] rounded-xl
          bg-white
          ring-1 ring-border dark:ring-border
          data-[state=open]:animate-in data-[state=open]:fade-in-0
          data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-top-2
          [&>button]:hidden"
        aria-label="Command menu"
      >
        <Command
          className="flex flex-col"
          loop
        >
          {/* ---- Search input ---- */}
          <div className="flex items-center gap-2 border-b border-border px-4">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <Command.Input
              ref={inputRef}
              value={busqueda}
              onValueChange={setBusqueda}
              className="flex-1 h-12 bg-transparent text-sm text-foreground
                placeholder:text-muted-foreground dark:placeholder:text-muted-foreground
                outline-none border-0 ring-0 focus:ring-0"
              placeholder={t('command.search_placeholder')}
            />
            <kbd className="hidden sm:inline-flex h-5 items-center gap-1 rounded border
              border-border bg-muted
              px-1.5 font-mono text-micro font-medium text-muted-foreground">
              {'\u2318'}K
            </kbd>
          </div>

          {/* ---- Results list ---- */}
          <Command.List
            className="max-h-[480px] overflow-y-auto overflow-x-hidden scroll-smooth
              overscroll-contain py-2"
          >
            <Command.Empty className="flex flex-col items-center justify-center px-5 py-12 text-center text-sm text-muted-foreground">
              <Search className="h-10 w-10 mb-3 text-muted-foreground" />
              <p className="font-medium">{t('command.no_results')}</p>
              <p className="mt-1 max-w-xs text-xs">{t('command.no_results_hint')}</p>
            </Command.Empty>

            {/* Entidades (facturas, clientes, …) desde GET /api/search. Van primero:
                quien teclea un nombre busca esa ficha, no una pantalla. */}
            {busqueda.trim().length >= MIN_CARACTERES && (
              <ResultadosBusqueda
                busqueda={busqueda.trim()}
                onSelect={(url) => runCommand(() => navigate(url))}
              />
            )}

            {/* Los grupos se filtran por permiso y los que quedan vacíos no se
                pintan: con separadores fijos, un grupo oculto dejaba dos líneas
                pegadas y un hueco sin encabezado. El separador va ANTES de cada
                grupo salvo el primero, así que depende de cuántos sobrevivan. */}
            {[
              [t('command.quick_actions'), visible(quickActions)],
              [t('command.documents'), visible(extraDocuments)],
              ...sharedGroups,
            ]
              .filter(([, items]) => items.length > 0)
              .map(([heading, items], idx) => (
                <Fragment key={heading}>
                  {idx > 0 && <Command.Separator className="mx-3 my-1 h-px bg-muted" />}
                  {renderGroup(heading, items)}
                </Fragment>
              ))}
          </Command.List>

          {/* ---- Footer hints ---- */}
          <div className="flex items-center gap-4 border-t border-border
            bg-muted/80 px-4 py-2 text-micro text-muted-foreground"
          >
            <span className="inline-flex items-center gap-1">
              <ArrowUp className="h-3 w-3" />
              <ArrowDown className="h-3 w-3" />
              <span className="ml-0.5">{t('command.hint_navigate')}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <CornerDownLeft className="h-3 w-3" />
              <span className="ml-0.5">{t('command.hint_select')}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="rounded border border-border px-1 font-mono text-nano">Esc</kbd>
              <span className="ml-0.5">{t('command.hint_close')}</span>
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
